<template>
  <button
    :aria-label="triggerLabel"
    :class="triggerClass"
    aria-haspopup="true"
    class="flex items-center transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
    type="button"
    @click="toggle"
  >
    <!-- The business, with the person signed in as a badge on its corner: this tenant, as this user. -->
    <span class="relative shrink-0">
      <TwnTenantMark
        :size="28"
        class="rounded-md text-xs"
      />
      <TwnAccountAvatar
        :size="16"
        class="absolute -bottom-1 -right-1 ring-2 ring-surface-0"
      />
    </span>
    <template v-if="!isCompact">
      <span class="flex min-w-0 flex-1 flex-col leading-tight">
        <span class="truncate text-[13px] font-semibold text-surface-900">{{ tenantName }}</span>
        <span class="truncate text-[11.5px] text-surface-500">{{ fullName }}</span>
      </span>
      <MaterialIcon
        class="text-surface-400"
        ligature="unfold_more"
        size="1rem"
      />
    </template>
  </button>

  <TwnMenu
    ref="menuRef"
    :dt="ACCOUNT_MENU_TOKENS"
    :model="items"
    :popup="true"
    :pt="{ itemLabel: { class: 'text-sm' } }"
    class="w-64"
  >
    <!-- One 16px inset for every block, the row icons' own, and EXO's light dividers between them. -->
    <template #start>
      <div class="flex items-center gap-3 px-4 py-3">
        <TwnAccountAvatar :size="32" />
        <div class="flex min-w-0 flex-col">
          <span class="truncate text-sm font-medium text-surface-900">{{ fullName }}</span>
          <span class="truncate text-xs text-surface-500">{{ authUser?.email }}</span>
        </div>
      </div>
      <!-- It stays open on a pick, so the change is seen as it lands. -->
      <div
        v-if="isDarkModeAvailable"
        class="flex flex-col gap-2 border-t border-surface-100 px-4 py-3"
      >
        <p class="flex items-center justify-between text-xs font-medium text-surface-500">
          Theme
          <span
            v-if="appearance === 'dark'"
            class="font-normal text-surface-400"
            >Dark is in beta</span
          >
        </p>
        <div
          aria-label="Theme"
          class="grid grid-cols-3 gap-0.5 rounded-[calc(0.5rem*var(--radius-scale,1)+2px)] bg-surface-100 p-0.5"
          role="group"
        >
          <button
            v-for="option in APPEARANCE_OPTIONS"
            :key="option.appearance"
            :aria-pressed="option.appearance === appearance"
            :class="
              option.appearance === appearance
                ? 'bg-surface-0 text-surface-900 shadow-sm ring-1 ring-surface-200/60'
                : 'text-surface-500 hover:text-surface-800'
            "
            class="flex h-7 items-center justify-center gap-1 rounded-md text-xs font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
            type="button"
            @click="setAppearance(option.appearance)"
          >
            <MaterialIcon
              :is-filled="option.appearance === appearance"
              :ligature="option.icon"
              size="0.875rem"
            />
            {{ option.label }}
          </button>
        </div>
      </div>
      <div class="border-t border-surface-100" />
    </template>
    <template #end>
      <div class="flex items-center gap-2 border-t border-surface-100 px-4 py-2.5">
        <!-- eslint-disable-next-line vue/no-restricted-html-elements -- Twine's own site, opened in a new tab -->
        <a
          aria-label="About Twine"
          class="flex items-center rounded p-0.5 hover:bg-surface-100"
          :href="TWINE_SITE_URL"
          rel="noopener noreferrer"
          target="_blank"
        >
          <NavyLogo class="block h-[11px] w-auto dark:hidden" />
          <PinkLogo class="hidden h-[11px] w-auto dark:block" />
        </a>
        <span class="flex-1" />
        <button
          v-tooltip="'Copy the version and support details'"
          class="flex items-center gap-1 rounded px-1 py-0.5 font-mono text-[10.5px] text-surface-600 hover:bg-surface-100 hover:text-surface-900"
          type="button"
          @click="copySupportDetails"
        >
          {{ versionLabel }}
          <MaterialIcon
            ligature="content_copy"
            size="0.75rem"
          />
        </button>
      </div>
    </template>
  </TwnMenu>
</template>

<script lang="ts" setup>
import type { MenuItem } from 'primevue/menuitem'

import { router } from '@inertiajs/vue3'
import { useQueryClient } from '@tanstack/vue-query'
import { computed, useTemplateRef } from 'vue'

import type { TwnFocusSidebarUserMenuProps } from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-user-menu/TwnFocusSidebarUserMenu.types'

import NavyLogo from '@/assets/svgs/navy-logo.svg?component'
import PinkLogo from '@/assets/svgs/pink-logo.svg?component'
import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'
import TwnMenu from '@/components/atoms/prime/menu/twn-menu/TwnMenu.vue'
import { useAuthUser } from '@/composables/use-auth-user/useAuthUser'
import TwnAccountAvatar from '@/domains/admin-panel/components/atoms/twn-account-avatar/TwnAccountAvatar.vue'
import {
  ACCOUNT_MENU_TOKENS,
  APPEARANCE_OPTIONS,
  LEGACY_DASHBOARD_PATH,
  TWINE_SITE_URL
} from '@/domains/admin-panel/components/molecules/menu/twn-focus-sidebar-user-menu/TwnFocusSidebarUserMenu.consts'
import { useAppearance } from '@/domains/admin-panel/composables/use-appearance/useAppearance'
import { buildSupportDetails } from '@/domains/admin-panel/utils/focus-sidebar/focusSidebar.utils'
import { useToastStore } from '@/domains/notifications/store/use-toast-store/useToastStore'
import TwnTenantMark from '@/domains/tenant/components/atoms/twn-tenant-mark/TwnTenantMark.vue'
import { useTenantName } from '@/domains/tenant/composables/use-tenant-name/useTenantName'
import { getUserFullName } from '@/domains/users/composables/use-user-helper/useUserHelper'

const props = withDefaults(defineProps<TwnFocusSidebarUserMenuProps>(), { isCompact: false })

// Set by `define` in vite.config.ts; absent under Vitest, where the build step does not run.
const APP_VERSION = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : ''

const menuRef = useTemplateRef<InstanceType<typeof TwnMenu>>('menuRef')
const queryClient = useQueryClient()
const toastStore = useToastStore()
const { user: authUser } = useAuthUser()
const { appearance, isDarkModeAvailable, setAppearance } = useAppearance()

const fullName = computed(() => getUserFullName(authUser.value))
const tenantName = useTenantName()
const versionLabel = computed(() => (APP_VERSION ? `v${APP_VERSION}` : 'Version'))
// Folded, the trigger is the avatar alone, so it names itself for screen readers.
const triggerLabel = computed(() =>
  props.isCompact ? `${tenantName.value}, account menu for ${fullName.value}` : undefined
)
const triggerClass = computed(() =>
  props.isCompact
    ? 'justify-center rounded-lg p-1 hover:bg-surface-200/60'
    : 'h-[52px] w-full gap-3 rounded-[12px] bg-surface-0 px-[10px] text-left shadow-sm ring-1 ring-surface-200 hover:ring-surface-300'
)

const items = computed<MenuItem[]>(() => [
  { label: 'Profile', icon: 'person', command: () => router.get(route('profile.show')) },
  { label: 'Legacy dashboard', icon: 'arrow_outward', command: () => router.visit(LEGACY_DASHBOARD_PATH) },
  { separator: true },
  {
    label: 'Log out',
    icon: 'logout',
    command: () => {
      queryClient.clear()
      router.post(route('logout'))
    }
  }
])

const toggle = (event: Event) => menuRef.value?.toggle(event)

// The copy runs inside the click handler, which the Clipboard API requires.
const copySupportDetails = async () => {
  const details = buildSupportDetails({
    version: APP_VERSION,
    host: window.location.host,
    userAgent: navigator.userAgent,
    now: new Date()
  })

  try {
    await navigator.clipboard.writeText(details)
    toastStore.add({ severity: 'success', summary: 'Copied for Support', detail: details })
  } catch {
    toastStore.addError({ details: `Copying did not work. Your version is ${versionLabel.value}.` })
  }
}
</script>
